/**
 * AWS Rekognition Service
 * 
 * Validates uploaded photos contain human faces and extracts metadata
 * for more accurate skin analysis.
 */

import { 
  RekognitionClient, 
  DetectFacesCommand, 
  DetectFacesCommandInput, 
  FaceDetail,
  Emotion 
} from '@aws-sdk/client-rekognition';
import type { CognitoUserSession } from 'amazon-cognito-identity-js';
import { createAuthedRekognitionClient } from '../config/aws';

export interface FaceMetadata {
  ageRange?: {
    low: number;
    high: number;
  };
  gender?: {
    value: 'Male' | 'Female';
    confidence: number;
  };
  emotions?: Array<{
    type: string;
    confidence: number;
  }>;
  boundingBox?: {
    width: number;
    height: number;
    left: number;
    top: number;
  };
  confidence: number;
  quality?: {
    brightness?: number;
    sharpness?: number;
  };
}

export interface RekognitionResult {
  isValid: boolean;
  faceCount: number;
  faces: FaceMetadata[];
  error?: string;
}

/**
 * Convert image blob to Uint8Array for Rekognition API
 */
const blobToUint8Array = (blob: Blob): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const arrayBuffer = reader.result as ArrayBuffer;
      resolve(new Uint8Array(arrayBuffer));
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(blob);
  });
};

/**
 * Detect faces in uploaded photo using AWS Rekognition
 * Validates that photo contains a human face and extracts metadata
 */
export async function detectFacesWithRekognition(
  session: CognitoUserSession,
  photoBlob: Blob,
  slot: 'front' | 'side'
): Promise<RekognitionResult> {
  try {
    const rekognition = createAuthedRekognitionClient(session);
    
    // Convert blob to Uint8Array for Rekognition API
    const imageBytes = await blobToUint8Array(photoBlob);

    // Prepare Rekognition request
    const params: DetectFacesCommandInput = {
      Image: {
        Bytes: imageBytes,
      },
      Attributes: [
        'ALL', // Get all available attributes: age range, gender, emotions, quality, etc.
      ],
    };

    const command = new DetectFacesCommand(params);
    const response = await rekognition.send(command);

    // Check if faces were detected
    if (!response.FaceDetails || response.FaceDetails.length === 0) {
      return {
        isValid: false,
        faceCount: 0,
        faces: [],
        error: slot === 'front' 
          ? 'This is not a face picture. Please upload a clear front-facing photo of your face and try again.'
          : 'This is not a face picture. Please upload a clear side profile photo of your face and try again.',
      };
    }

    // Extract face metadata
    const faces: FaceMetadata[] = (response.FaceDetails || []).map((face: FaceDetail) => {
      const emotions = (face.Emotions || []).map((emotion: Emotion) => ({
        type: emotion.Type || 'Unknown',
        confidence: emotion.Confidence || 0,
      }));

      // Sort emotions by confidence (highest first)
      emotions.sort((a: { confidence: number }, b: { confidence: number }) => b.confidence - a.confidence);

      return {
        ageRange: face.AgeRange
          ? {
              low: face.AgeRange.Low || 0,
              high: face.AgeRange.High || 0,
            }
          : undefined,
        gender: face.Gender
          ? {
              value: (face.Gender.Value || 'Unknown') as 'Male' | 'Female',
              confidence: face.Gender.Confidence || 0,
            }
          : undefined,
        emotions,
        boundingBox: face.BoundingBox
          ? {
              width: face.BoundingBox.Width || 0,
              height: face.BoundingBox.Height || 0,
              left: face.BoundingBox.Left || 0,
              top: face.BoundingBox.Top || 0,
            }
          : undefined,
        confidence: face.Confidence || 0,
        quality: {
          brightness: face.Quality?.Brightness,
          sharpness: face.Quality?.Sharpness,
        },
      } as FaceMetadata;
    });

    // Validate face quality
    const primaryFace = faces[0];
    if (primaryFace.confidence < 80) {
      return {
        isValid: false,
        faceCount: faces.length,
        faces,
        error: slot === 'front'
          ? 'Face detection quality is too low. Please upload a clearer front-facing photo with better lighting and try again.'
          : 'Face detection quality is too low. Please upload a clearer side profile photo with better lighting and try again.',
      };
    }

    // Check if face is too small (less than 10% of image)
    if (primaryFace.boundingBox) {
      const faceArea = primaryFace.boundingBox.width * primaryFace.boundingBox.height;
      if (faceArea < 0.1) {
        return {
          isValid: false,
          faceCount: faces.length,
          faces,
          error: slot === 'front'
            ? 'Your face is too small in the photo. Please move closer to the camera and ensure your face fills more of the frame, then try again.'
            : 'Your face is too small in the side photo. Please move closer to the camera and ensure your face fills more of the frame, then try again.',
        };
      }
    }

    // Check image quality
    if (primaryFace.quality) {
      if (primaryFace.quality.brightness !== undefined && primaryFace.quality.brightness < 50) {
        // Warning will be shown but not block
      }
      if (primaryFace.quality.sharpness !== undefined && primaryFace.quality.sharpness < 50) {
        // Warning will be shown but not block
      }
    }

    // Multiple faces detected - warn but allow if primary face is clear
    if (faces.length > 1) {
      console.warn(`Multiple faces detected (${faces.length}). Using primary face for analysis.`);
    }

    return {
      isValid: true,
      faceCount: faces.length,
      faces,
    };
  } catch (error: any) {
    console.error('Rekognition error:', error);
    
    // Handle specific AWS errors
    if (error.name === 'InvalidImageFormatException') {
      return {
        isValid: false,
        faceCount: 0,
        faces: [],
        error: 'Invalid image format. Please upload a JPG or PNG image and try again.',
      };
    }
    
    if (error.name === 'ImageTooLargeException') {
      return {
        isValid: false,
        faceCount: 0,
        faces: [],
        error: 'Image is too large. Please upload an image under 15MB and try again.',
      };
    }

    // For other errors, return error but don't block (fallback to basic validation)
    return {
      isValid: false,
      faceCount: 0,
      faces: [],
      error: error.message || 'Face detection service is temporarily unavailable. Please try again in a moment.',
    };
  }
}

/**
 * Validate both front and side photos have faces
 */
export async function validateScanPhotos(
  session: CognitoUserSession,
  frontPhoto: Blob,
  sidePhoto: Blob
): Promise<{
  front: RekognitionResult;
  side: RekognitionResult;
  isValid: boolean;
  errors: string[];
}> {
  const [frontResult, sideResult] = await Promise.all([
    detectFacesWithRekognition(session, frontPhoto, 'front'),
    detectFacesWithRekognition(session, sidePhoto, 'side'),
  ]);

  const errors: string[] = [];
  if (!frontResult.isValid && frontResult.error) {
    errors.push(`Front photo: ${frontResult.error}`);
  }
  if (!sideResult.isValid && sideResult.error) {
    errors.push(`Side photo: ${sideResult.error}`);
  }

  return {
    front: frontResult,
    side: sideResult,
    isValid: frontResult.isValid && sideResult.isValid,
    errors,
  };
}

